/**
 * Fingerprint perangkat sederhana tanpa dependensi eksternal.
 *
 * Nilainya dipakai backend (kolom login_activity.fingerprint) untuk mengenali
 * apakah sebuah login datang dari perangkat yang sudah pernah dipakai atau belum.
 *
 * Strategi: nilai dihitung sekali dari karakteristik browser lalu DISIMPAN di
 * localStorage. Pembacaan berikutnya memakai nilai tersimpan itu, bukan menghitung
 * ulang. Ini disengaja — sinyal seperti versi browser atau resolusi layar berubah
 * seiring waktu, dan kalau dihitung ulang tiap login, perangkat yang sama akan
 * terus terbaca sebagai perangkat baru.
 *
 * Catatan: ini bukan identitas yang bisa dipercaya untuk keputusan keamanan.
 * Nilainya berasal dari client sehingga bisa dipalsukan, dan akan hilang bila
 * pengguna membersihkan site data atau memakai mode penyamaran. Gunakan hanya
 * sebagai petunjuk pengenalan perangkat, bukan sebagai faktor autentikasi.
 */

const STORAGE_KEY = "bc_device_fp";

/**
 * Hash 32-bit FNV-1a, dikembalikan sebagai 8 karakter hex.
 */
function fnv1a(str) {
	let hash = 0x811c9dc5;
	for (let i = 0; i < str.length; i++) {
		hash ^= str.charCodeAt(i);
		hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
	}
	return ("00000000" + hash.toString(16)).slice(-8);
}

/**
 * Hash djb2, dipakai berdampingan dengan FNV-1a supaya ruang nilai gabungannya
 * lebih lebar dan peluang tabrakan antar perangkat jauh lebih kecil.
 */
function djb2(str) {
	let hash = 5381;
	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
	}
	return ("00000000" + hash.toString(16)).slice(-8);
}

/**
 * Kumpulkan karakteristik browser yang relatif stabil dalam satu string.
 */
function collectSignals() {
	const nav = window.navigator || {};
	const scr = window.screen || {};

	let timezone = "";
	try {
		timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
	} catch (e) {
		timezone = "";
	}

	return [
		nav.userAgent || "",
		nav.language || "",
		(nav.languages || []).join(","),
		nav.platform || "",
		nav.hardwareConcurrency || "",
		nav.deviceMemory || "",
		nav.maxTouchPoints || 0,
		scr.width || "",
		scr.height || "",
		scr.colorDepth || "",
		new Date().getTimezoneOffset(),
		timezone,
	].join("|");
}

/**
 * Bagian acak, supaya dua perangkat dengan spesifikasi identik (misalnya dua
 * laptop keluaran sama dengan browser versi sama) tetap menghasilkan nilai berbeda.
 */
function randomPart() {
	const buf = new Uint8Array(8);
	if (window.crypto && window.crypto.getRandomValues) {
		window.crypto.getRandomValues(buf);
	} else {
		for (let i = 0; i < buf.length; i++) {
			buf[i] = Math.floor(Math.random() * 256);
		}
	}
	return Array.from(buf)
		.map((b) => ("0" + b.toString(16)).slice(-2))
		.join("");
}

/**
 * Ambil fingerprint perangkat saat ini.
 *
 * @returns {string} 32 karakter hex, atau string kosong bila tidak bisa dihitung.
 */
export function getFingerprint() {
	try {
		const stored = window.localStorage.getItem(STORAGE_KEY);
		if (stored && /^[0-9a-f]{32}$/.test(stored)) {
			return stored;
		}

		const signals = collectSignals();
		const fp = fnv1a(signals) + djb2(signals) + randomPart();

		window.localStorage.setItem(STORAGE_KEY, fp);
		return fp;
	} catch (e) {
		// localStorage bisa melempar error di mode penyamaran atau saat kuota penuh.
		// Login tidak boleh gagal hanya karena fingerprint tidak bisa disimpan.
		try {
			const signals = collectSignals();
			return fnv1a(signals) + djb2(signals) + randomPart();
		} catch (err) {
			return "";
		}
	}
}

export default getFingerprint;
